import { LOGGING } from '../../../config/client.config';

const pushData = (data, timeout) =>
  new Promise((resolve) => {
    if (!process.browser) {
      if (LOGGING) {
        console.info('Ignoring dataLayer.push request on server side', data); // eslint-disable-line
      }
      resolve();
      return;
    }

    let schedule = null;

    const callback = () => {
      if (schedule) {
        clearTimeout(schedule);
        schedule = null;
        callback();
        resolve();
      }
    };

    setTimeout(callback, timeout || 1000);

    // Google data layer push
    global.dataLayer.push({
      ...data,
      eventCallback: callback,
    });

    if (LOGGING) {
      console.info('dataLayer.push', data); // eslint-disable-line
    }
  });

const virtualTrackPage = (page) => {
  pushData({
    event: 'virtualTrackPage',
    page,
  });
};

const ecommerceEvent = (action, ecommerce) =>
  pushData({
    event: 'analyticsEvent',
    eventCategory: 'Ecommerce',
    eventAction: action,
    ecommerce,
  });

const getReferenceTotal = (cart, name) =>
  cart.totals
    .filter(({ id }) => id.substring(0, name.length) === name)
    .map(({ valueInEuro }) => valueInEuro)
    .reduce((previous, current) => previous + current, 0);

const getTotal = (cart, name) =>
  cart.totals
    .filter(({ id }) => id.substring(0, name.length) === name)
    .map(({ value }) => value)
    .reduce((previous, current) => previous + current, 0);

const funnelEvent = (step, cart) =>
  pushData({
    event: 'funnel',
    step,
    type: cart.type,
    products: cart.items.map((item) => ({
      productName: item.product.title,
      productId: item.product.masterSku || item.product.sku,
      quantity: item.amount,
      price: item.totalPrice,
      category: item.product.brand,
      subCategory: item.product.category,
    })),
  });

const handleCommerceEvent = (step, cart) => {
  ecommerceEvent('Checkout', {
    checkout: {
      actionField: {
        step,
        option: cart.paymentMethod && cart.paymentMethod.method,
      },
      products: cart.items.map((item) => ({
        name: item.product.title,
        id: item.product.masterSku || item.product.sku,
        quantity: item.amount,
        price: item.totalInEuro,
        brand: item.product.brand,
        category: item.product.category,
      })),
    },
  });
};

const languageEvent = (lang, country) => {
  pushData({
    lang,
    country,
  });
};

const formatPrice = (price) => Number.parseFloat(price).toFixed(2);

const orderCreatedEvent = ({ language, hash, customer, order }) =>
  Promise.all([
    pushData({
      event: 'transaction',
      transactionId: order.id,
      transactionType: 'purchase',
      transactionAffiliation: 'Native Instruments',
      transactionTotal: getReferenceTotal(order, 'grand_total'),
      transactionTax: getReferenceTotal(order, 'tax'),
      transactionShipping: getReferenceTotal(order, 'shipping'),
      transactionPaymentType: order.paymentMethod.title,
      transactionCurrency: order.currency,
      transactionProducts: order.items.map((item) => ({
        id: item.product.sku,
        sku: item.product.sku,
        master: item.product.masterSku,
        name: item.product.title,
        price: item.totalInEuro,
        quantity: item.amount,
        brand: item.product.brand,
        category: item.product.category,
      })),
      transactionCity: order.billingAddress.city,
      transactionState: order.billingAddress.regionCode,
      transactionCountry: order.billingAddress.countryCode,
      lang: language,
      total: formatPrice(getTotal(order, 'grand_total')),
      subtotal: formatPrice(getTotal(order, 'subtotal')),
      purchase: formatPrice(order.subTotalInclTaxInEur),
      hold: order.onHold,
      quantity: order.amount,
      discount: formatPrice(order.discountAmount),
      storeId: order.storeNumber,
      customerLastRegistrationDate: customer.lastRegistrationDate,
      uniqueUserHash: hash,
      customerLastOnlineOrderDate: order.previousOrderDate,
    }),

    ecommerceEvent('Purchase', {
      purchase: {
        actionField: {
          id: order.id,
          affiliation: 'Native Instruments',
          revenue: formatPrice(getReferenceTotal(order, 'grand_total')),
          tax: formatPrice(getReferenceTotal(order, 'tax')),
          shipping: formatPrice(getReferenceTotal(order, 'shipping')),
          coupon: order.couponCode,
        },
        products: order.items.map((item) => ({
          id: item.product.sku,
          sku: item.product.sku,
          master: item.product.masterSku,
          name: item.product.title,
          price: item.totalInEuro,
          quantity: item.amount,
          brand: item.product.brand,
          category: item.product.category,
        })),
      },
    }),
  ]);

export {
  funnelEvent,
  handleCommerceEvent,
  orderCreatedEvent,
  languageEvent,
  virtualTrackPage,
  pushData,
};
